import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightbulb, CalendarDays, ChevronRight, Sparkles, Star, PhoneCall, Columns3 } from "lucide-react";
import { api } from "../../lib/api";
import { BlocksView } from "../../components/Blocks";
import { ProgIcon } from "../../components/ProgIcon";

export default function ElevateHub() {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [calLocked, setCalLocked] = useState(false);
  const [avisLocked, setAvisLocked] = useState(true);
  const [bookLocked, setBookLocked] = useState(true);
  const [closingLocked, setClosingLocked] = useState(true);
  const [crmLocked, setCrmLocked] = useState(true);

  useEffect(() => {
    api.get("/client/hub-resources").then(({ data }) => setResources(data)).catch(() => {}).finally(() => setLoaded(true));
    api.get("/client/content-space").then(({ data }) => setCalLocked(!!data.locked)).catch(() => {});
    api.get("/client/hub-pages").then(({ data }) => { setAvisLocked(!!data.avis_locked); setBookLocked(!!data.booking_locked); setClosingLocked(!!data.closing_locked); setCrmLocked(!!data.crm_locked); }).catch(() => {});
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-6">
      <div>
        <p className="eyebrow">Ton espace ressources</p>
        <h1 className="mt-2 font-sans text-3xl font-medium text-ink sm:text-4xl">The elevate hub</h1>
      </div>

      <div data-testid="hub-intro-note" className="flex gap-4 rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-5 sm:p-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-bordeaux">
          <Lightbulb size={20} />
        </span>
        <p className="font-light leading-relaxed text-ink-soft">
          Dans cette section, je vais te partager des ressources adaptées à tes besoins et à tes objectifs. C'est ici qu'on
          va détailler les actions spécifiques à mettre en place, en fonction de tes besoins et de ce que nous avons construit
          ensemble jusqu'à maintenant.
        </p>
      </div>

      {(!calLocked || !avisLocked || !bookLocked || !closingLocked || !crmLocked) && (
        <div className="space-y-3">
          <p className="eyebrow mb-2">Tes pages</p>
          {!calLocked && <button
            data-testid="hub-content-space"
            onClick={() => navigate("/espace/creation-contenu")}
            className="group flex w-full items-center gap-4 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition hover:border-bordeaux-border hover:bg-bordeaux-tint/20 sm:p-5"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux">
              <CalendarDays size={20} />
            </span>
            <span className="flex-1">
              <span className="block font-sans text-base font-medium text-ink">Espace de création de contenu</span>
              <span className="block text-sm font-light text-ink-muted">Ouvrir ton calendrier éditorial : planifie et rédige tes posts du mois</span>
            </span>
            <ChevronRight size={18} className="shrink-0 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-bordeaux" />
          </button>}
          {!avisLocked && (
            <button data-testid="hub-avis-page" onClick={() => navigate("/espace/hub/avis")}
              className="group flex w-full items-center gap-4 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition hover:border-bordeaux-border hover:bg-bordeaux-tint/20 sm:p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><Star size={20} /></span>
              <span className="flex-1">
                <span className="block font-sans text-base font-medium text-ink">Récolte des BONS avis (process)</span>
                <span className="block text-sm font-light text-ink-muted">Récupérer des témoignages pendant et à la fin de l'accompagnement, et faire une bonne étude de cas</span>
              </span>
              <ChevronRight size={18} className="shrink-0 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-bordeaux" />
            </button>
          )}
          {!bookLocked && (
            <button data-testid="hub-bookcall-page" onClick={() => navigate("/espace/hub/book-a-call")}
              className="group flex w-full items-center gap-4 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition hover:border-bordeaux-border hover:bg-bordeaux-tint/20 sm:p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><PhoneCall size={20} /></span>
              <span className="flex-1">
                <span className="block font-sans text-base font-medium text-ink">Tutoriel : Comment créer ta page book a call (étape par étape)</span>
                <span className="block text-sm font-light text-ink-muted">Rédaction, design et mise en ligne de ta page en 1H top chrono ⏱️</span>
              </span>
              <ChevronRight size={18} className="shrink-0 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-bordeaux" />
            </button>
          )}
          {!closingLocked && (
            <button data-testid="hub-closing-page" onClick={() => navigate("/espace/hub/closing")}
              className="group flex w-full items-center gap-4 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition hover:border-bordeaux-border hover:bg-bordeaux-tint/20 sm:p-5">
              <ProgIcon slug="strategie-vente" tint />
              <span className="flex-1">
                <span className="block font-sans text-base font-medium text-ink">Feedback de tes calls de closing</span>
                <span className="block text-sm font-light text-ink-muted">Partage le lien de tes appels et retrouve le retour de Fanny</span>
              </span>
              <ChevronRight size={18} className="shrink-0 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-bordeaux" />
            </button>
          )}
          {!crmLocked && (
            <button data-testid="hub-crm-page" onClick={() => navigate("/espace/hub/crm")}
              className="group flex w-full items-center gap-4 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition hover:border-bordeaux-border hover:bg-bordeaux-tint/20 sm:p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><Columns3 size={20} /></span>
              <span className="flex-1">
                <span className="block font-sans text-base font-medium text-ink">Ton CRM de setting</span>
                <span className="block text-sm font-light text-ink-muted">Suis tes prospects, tes relances et tes ventes</span>
              </span>
              <ChevronRight size={18} className="shrink-0 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-bordeaux" />
            </button>
          )}
        </div>
      )}

      {resources.length > 0 && (
        <div className="space-y-4">
          <p className="eyebrow mb-1">Tes ressources personnalisées</p>
          {resources.map((r) => (
            <div key={r.id} data-testid={`hub-resource-${r.id}`} className="rounded-2xl border border-line bg-white p-5 shadow-card sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux">
                  <Sparkles size={18} />
                </span>
                <div>
                  <p className="font-sans text-base font-medium text-ink">{r.title}</p>
                  {r.subtitle && <p className="text-sm font-light text-ink-muted">{r.subtitle}</p>}
                </div>
              </div>
              {r.blocks && r.blocks.length > 0 && <div className="mt-4"><BlocksView blocks={r.blocks} /></div>}
            </div>
          ))}
        </div>
      )}

      {loaded && resources.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-white/60 p-10 text-center">
          <p className="text-ink-muted">Tes ressources personnalisées apparaîtront ici au fil de ton accompagnement ✨</p>
        </div>
      )}
    </motion.div>
  );
}
